import { isEmpty, isNil } from 'lodash';

/**
 * The in-app path an href points at (`/maintenance/cards/abc123?x=1#y`), or undefined when the
 * href leaves the app.
 *
 * Internal means this app, on this data: a root-relative path, or an absolute URL on any origin
 * that serves this session — the one the page came from, `VITE_APP_URL`, or
 * `VITE_SERVER_RESPONSE_URL`. Anything else — another environment, another site — is external.
 *
 * Callers use the result for both halves of the decision: an internal href navigates through
 * the router in the current window, an external one opens in a new tab.
 */
export function internalAppPath(href: string): string | undefined {
  const trimmed = href.trim();
  // Protocol-relative (`//host/path`) names another origin, not a path on this one.
  if (isEmpty(trimmed) || trimmed.startsWith('//')) {
    return undefined;
  }

  let url: URL;
  try {
    url = new URL(trimmed, window.location.origin);
  } catch {
    return undefined;
  }

  if (!isThisApp(url)) {
    return undefined;
  }

  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * `mailto:`/`tel:` and other opaque schemes have an origin of `'null'`, which matches no real
 * origin, so they fall out here as external without a scheme check of their own.
 */
function isThisApp(url: URL): boolean {
  return (
    url.origin === window.location.origin ||
    url.origin === envUrl('VITE_APP_URL')?.origin ||
    url.origin === envUrl('VITE_SERVER_RESPONSE_URL')?.origin
  );
}

function envUrl(name: 'VITE_APP_URL' | 'VITE_SERVER_RESPONSE_URL'): URL | undefined {
  // `VITE_SERVER_RESPONSE_URL` is unset in the environments that don't need it.
  const value = import.meta.env[name];
  if (isNil(value) || isEmpty(value)) {
    return undefined;
  }
  try {
    return new URL(value);
  } catch {
    return undefined;
  }
}
